import StatusBadge from "@/components/StatusBadge";
import JobAttachments from "@/components/JobAttachments";
import { Button } from "@/components/ui/button";
import { startCheckout } from "@/lib/payments";
import { typesLabel, pricingLabel } from "@/lib/jobOptions";
import { Users, MapPin, Euro, Calendar, Trash2, Pencil, CalendarClock } from "lucide-react";

const JOB_MAX_EXTENSIONS = 2;

function ExpiryRow({ j, onExtend }) {
  if (!j.vervalt_op || j.status === "concept") return null;
  const verval = new Date(j.vervalt_op);
  const days = Math.ceil((verval.getTime() - Date.now()) / 86400000);
  const soon = days <= 5;
  const maxed = (j.verleng_count || 0) >= JOB_MAX_EXTENSIONS;
  const dateStr = verval.toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div data-testid="job-expiry-row" className={`mb-3 rounded-sm px-3 py-2 text-xs flex flex-wrap items-center gap-2 ${soon ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-slate-50 border border-slate-200 text-slate-600"}`}>
      <CalendarClock className="w-3.5 h-3.5 shrink-0" />
      <span className="font-semibold">Verloopt op {dateStr}{days >= 0 ? ` (${days} dag${days === 1 ? "" : "en"})` : " (verlopen)"}</span>
      {!maxed ? (
        <Button data-testid="extend-job-btn" onClick={() => onExtend?.(j.id)} size="sm"
          className="ml-auto h-7 bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold text-xs px-3">
          Verleng +1 maand
        </Button>
      ) : (
        <span className="ml-auto text-slate-400">Max. verlengd</span>
      )}
    </div>
  );
}

export function JobCard({ job: j, onView, onEdit, onDelete, onExtend }) {
  return (
    <div data-testid="company-job-card" className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-display text-lg font-bold text-slate-950">{j.titel}</h3>
        <StatusBadge status={j.status} />
      </div>
      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{j.beschrijving}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(j.types || []).map((t) => (
          <span key={t} className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">{typesLabel({ types: [t] })}</span>
        ))}
        {j.type_andere && <span className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">{j.type_andere}</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-4">
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.locatie}</span>
        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {j.start_datum}</span>
        <span className="flex items-center gap-1 col-span-2"><Euro className="w-3.5 h-3.5" /> {pricingLabel(j)}</span>
      </div>
      <ExpiryRow j={j} onExtend={onExtend} />
      <JobAttachments bijlagen={j.bijlagen} />
      {j.status === "concept" && (
        <div data-testid="job-pay-block" className="mb-2 bg-amber-50 border border-amber-200 rounded-sm p-3">
          <p className="text-xs font-semibold text-amber-800 mb-2">Deze opdracht is nog niet live. Betaal €3 om hem zichtbaar te maken voor professionals.</p>
          <Button data-testid="pay-job-btn" onClick={() => startCheckout({ kind: "job", jobId: j.id })}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
            <Euro className="w-4 h-4 mr-1" /> Betaal €3 en zet live
          </Button>
        </div>
      )}
      {j.monteur_name && (
        <p className="text-sm mb-3"><span className="text-slate-500">Gekozen professional:</span> <span className="font-semibold text-slate-900">{j.monteur_name}</span></p>
      )}
      <Button data-testid="view-interesses-btn" onClick={() => onView(j)}
        className="w-full mb-2 bg-slate-950 hover:bg-orange-500 text-white rounded-sm font-bold transition-colors">
        <Users className="w-4 h-4 mr-2" /> Geïnteresseerde professionals
      </Button>
      <div className="flex gap-2">
        <Button data-testid="edit-job-btn" onClick={() => onEdit(j)} variant="outline"
          className="flex-1 rounded-sm font-semibold border-slate-300 hover:border-orange-400 hover:text-orange-600">
          <Pencil className="w-4 h-4 mr-2" /> Aanpassen
        </Button>
        <Button data-testid="delete-job-btn" onClick={() => onDelete(j.id)} variant="outline"
          className="flex-1 rounded-sm font-semibold border-slate-300 text-red-600 hover:bg-red-50">
          <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
        </Button>
      </div>
    </div>
  );
}
